import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { KeyRound, Loader2, Lock, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import { signerConnexion, type DefiConnexion } from '../utils/cleSecurite';

const schema = yup.object({
  // Une adresse e-mail dans presque tous les cas — mais pas toujours : les
  // comptes de démonstration s'ouvrent avec un identifiant court, et le
  // serveur reste seul juge de ce qu'il accepte. Exiger une adresse ici
  // fermait la porte avant même d'avoir demandé.
  email: yup.string().trim().required('L\'identifiant est requis'),
  password: yup.string().required('Le mot de passe est requis'),
});

type FormData = yup.InferType<typeof schema>;

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const perso = usePersonnalisation();
  // Les comptes à essayer : renseignés sur la vitrine de démonstration
  // seulement, absents sur l'instance d'un client.
  const comptesDemo: { libelle: string; identifiant: string; motDePasse: string }[] =
    (perso as any)?.demoIdentifiants ?? [];

  /*
    LA DISCIPLINE MONTREE, POUR QUE LE VISITEUR SACHE OU IL EST.

    Vingt-et-une demonstrations partagent la meme page de connexion, au mot
    pres. Un visiteur qui en ouvre trois dans trois onglets ne saurait pas
    laquelle il regarde — et « demo-tennis-de-table.gest-asso.com » dans une
    barre d'adresse ne se lit pas d'un coup d'oeil.

    Vide sur la demonstration sans registre sportif : « Essayez librement la
    demo » suffit alors, et « la demo  » avec deux espaces se verrait.
  */
  const sportDemo: string | null =
    (perso as { demonstration?: { sport: string | null } | null })?.demonstration?.sport ?? null;

  // Une session expirée renvoie ici avec l'adresse qu'on regardait : on y
  // retourne après la reconnexion plutôt que de repartir de Mon espace.
  // Seuls les chemins internes sont acceptés — un ?retour=https://ailleurs
  // ferait de cette page un tremplin vers n'importe quel site.
  //
  // Sans adresse de retour, on arrive sur l'accès rapide : c'est l'écran des
  // gestes du jour — le scan, le planning, la messagerie — là où « Mon
  // espace » ne porte que ses propres coordonnées, qu'on vient corriger deux
  // fois l'an. Arriver sur ce qu'on ne fait presque jamais oblige à un clic
  // de plus à chaque connexion.
  const retour = params.get('retour');
  const destination = retour?.startsWith('/') && !retour.startsWith('//') ? retour : '/acces-rapide';
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  // La seconde étape n'apparaît que si le serveur la réclame : le mot de
  // passe est alors bon, il manque le code de l'application.
  const [secondeEtape, setSecondeEtape] = useState(false);
  const [code, setCode] = useState('');
  const [appareilDeConfiance, setAppareilDeConfiance] = useState(false);

  /*
   * Le défi de la clé, quand le serveur en réclame une.
   *
   * Il arrive DANS le refus du mot de passe, et non par un appel séparé : le
   * refus détruit la session, et un second appel n'aurait donc plus rien pour
   * retrouver de quel membre il s'agit.
   */
  const [defiCle, setDefiCle] = useState<DefiConnexion | null>(null);
  const [cleEnCours, setCleEnCours] = useState(false);

  /**
   * La connexion, avec ou sans second facteur.
   *
   * `cle` n'est fourni qu'au second passage : le premier sert à savoir si le
   * compte en réclame une, et le serveur répond alors par un refus qui porte le
   * défi.
   */
  const tenter = async (data: FormData, cle?: Record<string, string>) => {
    try {
      await login({
        email: data.email,
        password: data.password,
        code: secondeEtape && cle === undefined ? code : undefined,
        fiabiliserAppareil: secondeEtape ? appareilDeConfiance : undefined,
        cle,
      });
      navigate(destination, { replace: true });
      return true;
    } catch (e) {
      const echec = e as Error & {
        donnees?: {
          besoin2fa?: boolean;
          besoinCle?: boolean;
          cle?: DefiConnexion;
          message?: string;
        };
      };

      if (echec.donnees?.besoinCle === true && echec.donnees.cle !== undefined) {
        setSecondeEtape(true);
        setDefiCle(echec.donnees.cle);
        setError('root', { message: '' });
        return false;
      }

      if (echec.donnees?.besoin2fa === true) {
        setSecondeEtape(true);
        setError('root', {
          message: code === '' ? '' : (echec.donnees.message ?? 'Ce code ne correspond pas.'),
        });
        return false;
      }

      // Un refus de clé porte son motif : « signature refusée », « défi
      // expiré ». Le remplacer par « identifiants incorrects » enverrait
      // vérifier un mot de passe qui est bon.
      if (defiCle !== null && echec.donnees?.message) {
        setDefiCle(null);
        setError('root', { message: echec.donnees.message });
        return false;
      }

      setSecondeEtape(false);
      setDefiCle(null);
      setCode('');
      setError('root', { message: 'Identifiants incorrects.' });
      return false;
    }
  };

  const onSubmit = async (data: FormData) => {
    /*
     * Une clé attend d'être touchée : c'est ELLE qu'on présente, pas le
     * formulaire. Le mot de passe est déjà bon — le renvoyer sans signature
     * redemanderait un défi et ferait tourner en rond.
     */
    if (defiCle !== null) {
      setCleEnCours(true);
      try {
        const signee = await signerConnexion(defiCle);
        await tenter(data, signee);
      } catch (e) {
        const echec = e as Error & { name?: string };
        setError('root', {
          message:
            echec.name === 'NotAllowedError'
              ? 'Aucune clé n’a été touchée. Rebranchez-la et recommencez.'
              : (echec.message || 'La clé n’a pas répondu.'),
        });
      } finally {
        setCleEnCours(false);
      }
      return;
    }

    await tenter(data);
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 pb-24 lg:pb-0 max-w-md mx-auto">
      <h1 className="text-2xl lg:text-3xl font-bold text-center">Connexion</h1>

      {/* La vitrine de démonstration donne ses accès : on vient pour essayer,
          pas pour deviner un identifiant. Le bouton remplit les deux champs. */}
      {comptesDemo.length > 0 && (
        <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/10 p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-600" />
            {sportDemo === null ? 'Essayez librement' : `Essayez librement la démo ${sportDemo}`}
          </p>
          <ul className="space-y-2">
            {comptesDemo.map((c) => (
              <li key={c.identifiant} className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">{c.libelle}</span> — identifiant{' '}
                  <code className="font-mono bg-white dark:bg-gray-900 rounded px-1.5 py-0.5 border border-gray-200 dark:border-gray-700">
                    {c.identifiant}
                  </code>{' '}
                  , mot de passe{' '}
                  <code className="font-mono bg-white dark:bg-gray-900 rounded px-1.5 py-0.5 border border-gray-200 dark:border-gray-700">
                    {c.motDePasse}
                  </code>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setValue('email', c.identifiant);
                    setValue('password', c.motDePasse);
                  }}
                  className="btn-outline text-xs px-3 py-1.5 whitespace-nowrap"
                >
                  Remplir
                </button>
              </li>
            ))}
          </ul>

          {/*
            CE QU'IL ADVIENT DE CE QU'ON SAISIT, DIT AVANT DE SAISIR.

            Un visiteur qui passe une heure a monter sa saison et retrouve la
            demonstration vide quinze jours plus tard en conclut que le logiciel
            perd les donnees. Le dire ici retourne la phrase : ce n'est pas une
            perte, c'est une invitation a ne rien menager.
          */}
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Tout est modifiable, et rien n'est fragile : cette démonstration revient à son état de
            départ le 1er et le 15 de chaque mois. Créez, supprimez, essayez — vous ne casserez
            rien.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="relative">
          <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10"
            type="text"
            inputMode="email"
            autoComplete="username"
            placeholder="Adresse e-mail"
            {...register('email')}
          />
        </div>
        {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}

        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            className="input pl-10"
            type="password"
            placeholder="Mot de passe"
            {...register('password')}
          />
        </div>
        {errors.password && <p className="text-xs text-red-600">{errors.password.message}</p>}

        {/* La seconde étape : le mot de passe est passé, reste le code à six
            chiffres de l'application d'authentification — ou l'un des codes
            de secours remis à l'activation. */}
        {/*
          La clé attend d'être touchée : on ne montre QUE cela.

          Afficher en même temps le champ du code ferait hésiter entre deux
          gestes alors qu'un seul est demandé — et le champ, qui a le curseur,
          l'emporterait sur la clé, qui n'a rien pour attirer l'œil.
        */}
        {defiCle !== null && (
          <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/10 p-4 space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary-600" />
              <span>
                Ce compte est protégé par une clé de sécurité.{' '}
                <strong>Branchez-la et touchez-la</strong> pour terminer la connexion.
              </span>
            </p>
            {cleEnCours && (
              <p className="text-sm text-primary-800 dark:text-primary-300 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                En attente de votre clé…
              </p>
            )}
          </div>
        )}

        {secondeEtape && defiCle === null && (
          <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/10 p-4 space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary-600" />
              <span>
                Ce compte est protégé par la connexion en deux étapes. Saisissez le code affiché
                par votre application d'authentification, ou l'un de vos codes de secours.
              </span>
            </p>
            <div className="relative">
              <KeyRound className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                className="input pl-10 tracking-widest"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                className="w-4 h-4 accent-primary-600"
                checked={appareilDeConfiance}
                onChange={(e) => setAppareilDeConfiance(e.target.checked)}
              />
              Ne plus demander de code sur cet appareil pendant 30 jours
            </label>
          </div>
        )}

        {errors.root?.message && <p className="text-sm text-red-600 text-center">{errors.root.message}</p>}

        <button type="submit" disabled={cleEnCours} className="btn-primary w-full disabled:opacity-60">
          {defiCle !== null
            ? (cleEnCours ? 'Touchez votre clé…' : 'Utiliser ma clé de sécurité')
            : (secondeEtape ? 'Valider le code' : 'Se connecter')}
        </button>

        <p className="text-center text-sm">
          <Link to="/mot-de-passe-oublie" className="text-primary-700 hover:underline">
            Mot de passe oublié ?
          </Link>
        </p>
      </form>
    </div>
  );
}
